import moment from 'moment';
import {
	formatDateForParam,
	encodeUTCAdjustedDate,
	numberWithCommas,
	getDefaultDateRanges,
} from '../../utils/DataFormatterUtils';
import {
	ALL_COMPARISON_CHART_KEYS,
	VIEW_MODE,
	PERFORMER_GROUP,
} from './LifeCycleAnalyticsConstants';
import {
	COMPARISON_MODE,
	ComparisonMode,
	ComparisonState,
	DEFAULT_COMPARISON_STATE,
	getComparisonCurrentColor,
	isValidCustomComparisonRange,
} from '../../utils/AnalyticsComparisonUtils';

/** Max calendar span for lifecycle analytics created/completed range filters (see clampDateRange). */
export const LIFECYCLE_DATE_RANGE_MAX_SPAN_MONTHS = 6;

export const WORK_HOURS_TYPE = {
	WORK_HOURS: 'WORK_HOURS',
	AFTER_HOURS: 'AFTER_HOURS',
} as const;

export const getDefaultFilters = () => ({
	problemTypeIds: [] as number[],
	createdAt: null as any,
	completedAt: [
		moment().subtract(LIFECYCLE_DATE_RANGE_MAX_SPAN_MONTHS, 'months').startOf('day'),
		moment().endOf('day'),
	] as any,
	isPM: undefined as undefined | 'true' | 'false',
	workHoursType: undefined as undefined | 'WORK_HOURS' | 'AFTER_HOURS',
});

export const formatSupplierName = (row) => {
	if (row && row.supplierFacilityName) {
		return row.supplierFacilityName;
	}
	return `Supplier #${row.supplierFacilityId}`;
};

export const toMinutes = (seconds) => {
	if (seconds === null || seconds === undefined) {
		return 0;
	}
	return Math.round((seconds / 60) * 100) / 100;
};

export const formatDurationShort = (seconds) => {
	if (seconds === null || seconds === undefined || seconds === 0) {
		return '0m';
	}
	const totalMinutes = Math.floor(seconds / 60);
	const hours = Math.floor(totalMinutes / 60);
	const days = Math.floor(hours / 24);
	const remainingHours = hours % 24;
	const remainingMinutes = totalMinutes % 60;

	if (days > 0 && remainingHours > 0) {
		return `${days}d ${remainingHours}h`;
	}
	if (days > 0) {
		return `${days}d`;
	}
	if (hours > 0 && remainingMinutes > 0) {
		return `${hours}h ${remainingMinutes}m`;
	}
	if (hours > 0) {
		return `${hours}h`;
	}
	return `${totalMinutes}m`;
};

export const getPerformerGroupByRank = (index, total) => {
	if (total <= 0) return PERFORMER_GROUP.TYPICAL;

	// For very small numbers, guarantee at least 1 top and 1 bottom (unless total is 1)
	const topCount = Math.max(1, Math.round(total / 3));
	const bottomStartIndex = Math.max(topCount, total - Math.max(1, Math.round(total / 3)));

	if (index < topCount) {
		return PERFORMER_GROUP.TOP;
	}
	if (index >= bottomStartIndex) {
		return PERFORMER_GROUP.BOTTOM;
	}
	return PERFORMER_GROUP.TYPICAL;
};

export const getPositionBasedColor = (index: number, total: number): string => {
	const ratio = total <= 1 ? 1 : index / (total - 1);
	const hue = Math.round(ratio * 120); // 0=red, 60=yellow, 120=green
	return `hsl(${hue}, 80%, 40%)`;
};

export const getPositionBasedBgColor = (index: number, total: number): string => {
	const ratio = total <= 1 ? 1 : index / (total - 1);
	const hue = Math.round(ratio * 120);
	return `hsl(${hue}, 70%, 95%)`;
};

export const filtersToQueryParams = (
	filters,
	selectedPriorityId,
	minWoCount,
	viewMode = VIEW_MODE.ALL
) => {
	const params: any = {};
	if (filters.problemTypeIds && filters.problemTypeIds.length > 0) {
		params.problemTypeIds = filters.problemTypeIds.join(',');
	}
	if (filters.createdAt && filters.createdAt[0] && filters.createdAt[1]) {
		params.createdAtStart = filters.createdAt[0].format('YYYY-MM-DD');
		params.createdAtEnd = filters.createdAt[1].format('YYYY-MM-DD');
	}
	if (filters.completedAt && filters.completedAt[0] && filters.completedAt[1]) {
		params.completedAtStart = filters.completedAt[0].format('YYYY-MM-DD');
		params.completedAtEnd = filters.completedAt[1].format('YYYY-MM-DD');
	} else if (filters.completedAt === null) {
		params.completedAtCleared = 'true';
	}
	if (selectedPriorityId !== null && selectedPriorityId !== undefined) {
		params.priorityId = selectedPriorityId;
	}
	if (minWoCount > 0) {
		params.minWoCount = minWoCount;
	}
	if (filters.isPM !== undefined) {
		params.isPM = filters.isPM;
	}
	if (filters.workHoursType !== undefined) {
		params.workHoursType = filters.workHoursType;
	}
	return params;
};

export const queryParamsToFilters = (queryParams) => {
	const filters = getDefaultFilters();
	if (queryParams.problemTypeIds) {
		filters.problemTypeIds = String(queryParams.problemTypeIds)
			.split(',')
			.map((id) => parseInt(id, 10))
			.filter((id) => !isNaN(id));
	}
	if (queryParams.createdAtStart && queryParams.createdAtEnd) {
		filters.createdAt = [
			moment(String(queryParams.createdAtStart), 'YYYY-MM-DD').startOf('day'),
			moment(String(queryParams.createdAtEnd), 'YYYY-MM-DD').endOf('day'),
		];
	}
	if (queryParams.completedAtStart && queryParams.completedAtEnd) {
		filters.completedAt = [
			moment(String(queryParams.completedAtStart), 'YYYY-MM-DD').startOf('day'),
			moment(String(queryParams.completedAtEnd), 'YYYY-MM-DD').endOf('day'),
		];
	} else if (queryParams.completedAtCleared === 'true') {
		filters.completedAt = null;
	}
	if (queryParams.isPM === 'true' || queryParams.isPM === 'false') {
		filters.isPM = queryParams.isPM;
	}
	if (
		queryParams.workHoursType === WORK_HOURS_TYPE.WORK_HOURS ||
		queryParams.workHoursType === WORK_HOURS_TYPE.AFTER_HOURS
	) {
		filters.workHoursType = queryParams.workHoursType;
	}
	return filters;
};

export const escapeHtml = (str) => {
	if (!str) return '';
	return String(str)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
};

/**
 * Renders a comparison tooltip body: one row per series with a swatch,
 * left-aligned label, and right-aligned value. Used by both the metric line
 * charts and the spend bar chart in comparison mode. Pass `dashed: true|false`
 * on a row to render a line swatch matching the legend (solid for the current
 * series, dashed for the previous); omit it to keep the default square swatch.
 */
export const buildSeriesComparisonTooltipHtml = (
	rows: Array<{ label: string; color: string; value: string; dashed?: boolean }>,
	options?: { minWidth?: number }
): string => {
	const minWidth = (options && options.minWidth) || 220;
	const buildSwatch = (color: string, dashed: boolean | undefined) => {
		const safeColor = escapeHtml(color);
		if (dashed === undefined) {
			return `<span style="display:inline-block;width:10px;height:10px;background:${safeColor};margin-right:8px;border-radius:2px;flex-shrink:0;"></span>`;
		}
		if (dashed) {
			return `<svg width="24" height="2" style="display:inline-block;margin-right:8px;flex-shrink:0;" aria-hidden="true"><line x1="0" y1="1" x2="24" y2="1" stroke="${safeColor}" stroke-width="2" stroke-dasharray="4 4"></line></svg>`;
		}
		return `<span style="display:inline-block;width:24px;height:2px;background:${safeColor};margin-right:8px;flex-shrink:0;"></span>`;
	};
	const body = rows
		.map(
			(row) => `
		<div style="display:flex;justify-content:space-between;align-items:center;gap:32px;padding:4px 16px;">
			<span style="display:inline-flex;align-items:center;font-family:Roboto,sans-serif;font-size:14px;line-height:22px;color:#757575;font-weight:400;">
				${buildSwatch(row.color, row.dashed)}
				${escapeHtml(row.label)}
			</span>
			<span style="font-family:Roboto,sans-serif;font-size:14px;line-height:22px;color:#757575;font-weight:500;white-space:nowrap;">${escapeHtml(
				row.value
			)}</span>
		</div>`
		)
		.join('');
	return `<div style="min-width:${minWidth}px;padding:8px 0;">${body}</div>`;
};

export const buildSupplierTooltipHtml = (name, woCount, responseSeconds, resolutionSeconds) => {
	return (
		'<div style="min-width:240px;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;">' +
		'<div style="display:flex;justify-content:space-between;align-items:center;padding:0px 14px 8px;border-bottom:1px solid #f0f0f0;">' +
		'<span style="font-weight:500;font-size:14px;color:#757575;">' +
		escapeHtml(name) +
		'</span>' +
		'<span style="font-size:12px;color:#00000073;margin-left:24px;white-space:nowrap;">' +
		numberWithCommas(woCount) +
		' WOs</span>' +
		'</div>' +
		'<div style="padding:8px 14px;">' +
		'<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">' +
		'<span style="font-size:12px;color:#757575;">Avg response time</span>' +
		'<span style="font-size:12px;color:#757575;margin-left:24px;white-space:nowrap;">' +
		formatDurationShort(responseSeconds) +
		'</span>' +
		'</div>' +
		'<div style="display:flex;justify-content:space-between;align-items:center;">' +
		'<span style="font-size:12px;color:#757575;">Avg resolution time</span>' +
		'<span style="font-size:12px;color:#757575;margin-left:24px;white-space:nowrap;">' +
		formatDurationShort(resolutionSeconds) +
		'</span>' +
		'</div>' +
		'</div>' +
		'</div>'
	);
};

export const buildSpendTooltipHtml = (monthTitle, formattedSpend, detailUrl, seriesColor?) => {
	const safeUrl = escapeHtml(detailUrl || '#');
	const accentColor = escapeHtml(seriesColor || getComparisonCurrentColor());
	return (
		'<div style="min-width:160px;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;">' +
		'<div style="padding:0 0 8px 0;">' +
		'<span style="display:inline-block;width:8px;height:8px;background:' +
		accentColor +
		';margin-right:6px;border-radius:2px;vertical-align:baseline;"></span>' +
		'<span style="font-weight:500;font-size:13px;color:#757575;">' +
		escapeHtml(monthTitle) +
		'</span>' +
		'</div>' +
		'<div style="height:1px;background:#d9d9d9;margin:0 0 8px 0;"></div>' +
		'<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">' +
		'<span style="font-size:12px;color:#757575;font-weight:400;">Spend</span>' +
		'<span style="font-size:12px;color:#757575;font-weight:500;white-space:nowrap;">' +
		escapeHtml(formattedSpend) +
		'</span>' +
		'</div>' +
		'<a href="' +
		safeUrl +
		'" style="font-size:12px;color:' +
		accentColor +
		';font-weight:400;text-decoration:none;cursor:pointer;">view details &rarr;</a>' +
		'</div>'
	);
};

export const buildMetricTooltipHtml = (monthTitle, metricLabel, formattedValue, seriesColor?) => {
	const accentColor = escapeHtml(seriesColor || getComparisonCurrentColor());
	return (
		'<div style="min-width:200px;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;">' +
		'<div style="padding:0 14px 8px;border-bottom:1px solid #f0f0f0;">' +
		'<span style="font-weight:500;font-size:14px;color:#757575;">' +
		escapeHtml(monthTitle) +
		'</span>' +
		'</div>' +
		'<div style="display:flex;justify-content:space-between;align-items:center;padding:8px 14px;">' +
		'<span style="display:inline-flex;align-items:center;font-size:12px;color:#757575;">' +
		'<span style="display:inline-block;width:8px;height:8px;background:' +
		accentColor +
		';margin-right:6px;border-radius:50%;"></span>' +
		escapeHtml(metricLabel) +
		'</span>' +
		'<span style="font-size:12px;color:#757575;margin-left:24px;white-space:nowrap;">' +
		escapeHtml(formattedValue) +
		'</span>' +
		'</div>' +
		'</div>'
	);
};

export const filtersToParams = (filters) => {
	const params: any = {};
	if (filters.problemTypeIds && filters.problemTypeIds.length > 0) {
		params.problemTypeIds = filters.problemTypeIds.join(',');
	}
	if (filters.createdAt && filters.createdAt[0] && filters.createdAt[1]) {
		params.createdAtStartDate = formatDateForParam(
			encodeUTCAdjustedDate(filters.createdAt[0], false)
		);
		params.createdAtEndDate = formatDateForParam(encodeUTCAdjustedDate(filters.createdAt[1], true));
	}
	if (filters.completedAt && filters.completedAt[0] && filters.completedAt[1]) {
		params.completedAtStartDate = formatDateForParam(
			encodeUTCAdjustedDate(filters.completedAt[0], false)
		);
		params.completedAtEndDate = formatDateForParam(
			encodeUTCAdjustedDate(filters.completedAt[1], true)
		);
	}
	if (filters.isPM !== undefined) {
		params.isPM = filters.isPM;
	}
	if (filters.workHoursType !== undefined) {
		params.workHoursType = filters.workHoursType;
	}
	return params;
};

export const getLifecycleDateRanges = () => {
	const ranges = getDefaultDateRanges();
	delete ranges['Last 1 year'];
	return ranges;
};

export const parseSafeInt = (value: any, fallback: any = 0) => {
	if (value === null || value === undefined || value === '') return fallback;
	const parsed = parseInt(String(value), 10);
	return isNaN(parsed) ? fallback : parsed;
};

export const parseSafeFloat = (value: any, fallback: any = 0) => {
	if (value === null || value === undefined || value === '') return fallback;
	const parsed = parseFloat(String(value));
	return isNaN(parsed) ? fallback : parsed;
};

/**
 * Rounds to one decimal for display-style metrics (percentages, chart labels).
 * Uses toFixed(1) + parseFloat; same caveats as any binary float rounding at edge cases.
 */
export const roundToOneDecimalPlace = (value: number): number => {
	if (value === null || value === undefined) {
		return 0;
	}
	const num = Number(value);
	if (isNaN(num)) {
		return 0;
	}
	return parseFloat(num.toFixed(1));
};

export const clampDateRange = (dates) => {
	if (!dates || !dates[0] || !dates[1]) return dates;
	const start = dates[0].clone();
	let end = dates[1].clone();
	// Cap end date to today
	const today = moment().endOf('day');
	if (end.isAfter(today)) {
		end = today;
	}
	// If range exceeds max span, adjust end date from start
	const maxEnd = moment.min(
		start.clone().add(LIFECYCLE_DATE_RANGE_MAX_SPAN_MONTHS, 'months'),
		today
	);
	if (end.isAfter(maxEnd, 'day')) {
		end = maxEnd;
	}
	return [start, end];
};

export const getLifecycleRangePickerDisabledDate = (pickerDates) => (current) => {
	if (!current) return false;
	if (current.isAfter(moment(), 'day')) return true;

	if (pickerDates) {
		const startDate = pickerDates[0];
		const endDate = pickerDates[1];
		const maxSpan = LIFECYCLE_DATE_RANGE_MAX_SPAN_MONTHS;

		if (startDate && !endDate) {
			const maxSelectableEnd = moment.min(startDate.clone().add(maxSpan, 'months'), moment());
			return current.isBefore(startDate, 'day') || current.isAfter(maxSelectableEnd, 'day');
		}

		if (!startDate && endDate) {
			const minSelectableStart = endDate.clone().subtract(maxSpan, 'months');
			return current.isAfter(endDate, 'day') || current.isBefore(minSelectableStart, 'day');
		}
	}
	return false;
};

export const extractSpendValue = (item) => {
	if (item === null || item === undefined) {
		return 0;
	}
	const raw =
		item.spend != null
			? item.spend
			: item.totalSpend != null
			? item.totalSpend
			: item.spendUsd != null
			? item.spendUsd
			: item.facilityCostUsd != null
			? item.facilityCostUsd
			: item.amount != null
			? item.amount
			: 0;
	return parseSafeFloat(raw);
};

/** Build the default {chartKey: ComparisonState} map with every chart set to NONE. */
export const getDefaultChartComparisons = (): Record<string, ComparisonState> => {
	const result: Record<string, ComparisonState> = {};
	ALL_COMPARISON_CHART_KEYS.forEach((key) => {
		result[key] = { ...DEFAULT_COMPARISON_STATE };
	});
	return result;
};

const COMPARISON_QUERY_KEY = (chartKey: string) => 'cmp_' + chartKey;
const COMPARISON_START_QUERY_KEY = (chartKey: string) => 'cmp_' + chartKey + '_start';
const COMPARISON_END_QUERY_KEY = (chartKey: string) => 'cmp_' + chartKey + '_end';

const isValidComparisonMode = (value: string): value is ComparisonMode => {
	return (
		value === COMPARISON_MODE.NONE ||
		value === COMPARISON_MODE.PREVIOUS_PERIOD ||
		value === COMPARISON_MODE.LAST_YEAR ||
		value === COMPARISON_MODE.CUSTOM
	);
};

export const chartComparisonsToQueryParams = (
	chartComparisons: Record<string, ComparisonState>
): Record<string, string> => {
	const params: Record<string, string> = {};
	ALL_COMPARISON_CHART_KEYS.forEach((chartKey) => {
		const state = chartComparisons[chartKey];
		if (!state || state.mode === COMPARISON_MODE.NONE) return;
		params[COMPARISON_QUERY_KEY(chartKey)] = state.mode;
		if (state.mode === COMPARISON_MODE.CUSTOM && state.customStart && state.customEnd) {
			params[COMPARISON_START_QUERY_KEY(chartKey)] = state.customStart.format('YYYY-MM-DD');
			params[COMPARISON_END_QUERY_KEY(chartKey)] = state.customEnd.format('YYYY-MM-DD');
		}
	});
	return params;
};

export const queryParamsToChartComparisons = (
	queryParams: Record<string, any>
): Record<string, ComparisonState> => {
	const result = getDefaultChartComparisons();
	ALL_COMPARISON_CHART_KEYS.forEach((chartKey) => {
		const rawMode = queryParams[COMPARISON_QUERY_KEY(chartKey)];
		if (!rawMode || typeof rawMode !== 'string' || !isValidComparisonMode(rawMode)) return;
		if (rawMode === COMPARISON_MODE.NONE) return;
		if (rawMode === COMPARISON_MODE.CUSTOM) {
			const rawStart = queryParams[COMPARISON_START_QUERY_KEY(chartKey)];
			const rawEnd = queryParams[COMPARISON_END_QUERY_KEY(chartKey)];
			if (!rawStart || !rawEnd) return;
			const start = moment(String(rawStart), 'YYYY-MM-DD').startOf('day');
			const end = moment(String(rawEnd), 'YYYY-MM-DD').endOf('day');
			if (!isValidCustomComparisonRange(start, end)) return;
			result[chartKey] = { mode: COMPARISON_MODE.CUSTOM, customStart: start, customEnd: end };
			return;
		}
		result[chartKey] = { mode: rawMode, customStart: null, customEnd: null };
	});
	return result;
};

/** Override completedAt range on the filter blob (used for fetching the comparison-shifted period). */
export const filtersWithCompletedAtRange = (
	filters: any,
	range: [moment.Moment, moment.Moment]
): any => ({
	...filters,
	completedAt: [range[0].clone().startOf('day'), range[1].clone().endOf('day')],
});
